/* eslint-disable operator-linebreak */
import React from 'react';
import { FormApi } from 'final-form';
import { FormControlLabel, FormControlLabelClassKey } from '@material-ui/core';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import TableDatePicker from 'ui/TableInputs/TableDatePicker/TableDatePicker';
import { AmountInput, InputWithSelect, Select, SelectInnerItem, SelectItem, Textarea } from 'ui/Input';
import { MAX_ACCOUNT_LENGTH, ACCOUNT_LENGTHS, TransferFrequencyList } from 'utils/constants';
import { onlyNCharacters, onlyNumbers } from 'utils/formatters';
import Switch from 'ui/Switch/Switch';
import { IAccount } from 'store/accounts.model';
import { IFormValues, ITransferActions, ITransfersState } from './ToFirstAtlantic.model';

type FormType = FormApi<IFormValues, Partial<IFormValues>>;

const renderSourceAccountField = (
  accounts: IAccount[],
  submitting: boolean,
) => (
  <Select name="sourceAccount" label="Source Account" disabled={submitting}>
    {accounts.map(({ accountNumber }) => (
      <SelectItem value={accountNumber} key={accountNumber}>
        {accountNumber}
      </SelectItem>
    ))}
  </Select>
);

const renderAmountField = (submitting: boolean, currency?: string) => (
  <AmountInput
    name="amount"
    label="Amount"
    currency={currency}
    disabled={submitting}
  />
);

export const renderScheduleControl = (
  toggleScheduleTransfer: () => void,
  toggleSchedule: boolean | null,
  classes: Partial<ClassNameMap<FormControlLabelClassKey>> | undefined,
) => (
  <FormControlLabel
    control={<Switch />}
    checked={toggleSchedule === null ? false : toggleSchedule}
    onChange={toggleScheduleTransfer}
    label="Schedule transfer for later"
    labelPlacement="end"
    classes={classes}
    name="scheduleTransfer"
  />
);

export const renderScheduleForm = (
  submitting: boolean,
  dateFilterChange: (name: string, value?: string | null | undefined) => void,
  handleClearDateInput: (name: string, value?: string | null | undefined) => void,
  values: IFormValues,
) => (
  <>
    <Select name="frequency" label="Frequency" disabled={submitting}>
      {TransferFrequencyList.map(({ label, value }) => (
        <SelectItem value={value} key={value}>
          {label}
        </SelectItem>
      ))}
    </Select>
    <TableDatePicker
      name="specialDate"
      label="Special Date"
      onChange={dateFilterChange}
      onClickClear={handleClearDateInput}
      disabled={submitting || values.frequency !== 'specialDate'}
    />
  </>
);

const renderNarrationField = (submitting: boolean) => (
  <Textarea
    name="narration"
    label="Narration"
    fieldProps={{
      parse: onlyNCharacters(25),
    }}
    disabled={submitting}
  />
);

export const renderAccountNumberField = (
  values: IFormValues,
  { transferBeneficiaries, isLookingUp, lastLookedUpAccount, extras }: ITransfersState,
  findBeneficiaryName: ITransferActions['findBeneficiaryName'],
  form: FormType,
) => {
  const { destinationAccountName } = extras;
  const { destinationAccountNo } = values;

  const fieldName = 'destinationAccountNo';

  const characterLimits = MAX_ACCOUNT_LENGTH;
  const showLoader = Boolean(!destinationAccountName && isLookingUp);

  const showMessage =
    ACCOUNT_LENGTHS.includes(destinationAccountNo?.length) &&
    Boolean(lastLookedUpAccount) &&
    lastLookedUpAccount === values.destinationAccountNo;

  const handleClear = () => {
    form.mutators.changeFieldValue(fieldName, '');
  };

  const handleBlur = () => {
    findBeneficiaryName(values);
  };

  const handleSelect = (event) => {
    const { value: accountNumber } = event.target as HTMLInputElement;
    form.mutators.changeFieldValue(fieldName, accountNumber);

    const beneficiary = transferBeneficiaries.find((ben) => ben.accountNumber === accountNumber);
    if (beneficiary) {
      form.mutators.changeFieldValue('destinationBankCode', beneficiary.bankCode);
      form.mutators.changeFieldValue('beneficiaryName', beneficiary.name);
    }

    findBeneficiaryName(form.getState().values);
  };

  return (
    <>
      <InputWithSelect
        name={fieldName}
        label="Account Number"
        isEmpty={!values.destinationAccountNo}
        fieldProps={{
          parse: (value: string) => onlyNCharacters(characterLimits)(onlyNumbers(value)),
        }}
        disabled={showLoader}
        isLoading={showLoader}
        showMessage={showMessage}
        message={destinationAccountName}
        onClear={handleClear}
        onBlur={handleBlur}
        onSelectItemChange={handleSelect}
      >
        {transferBeneficiaries.map(({ name, accountNumber }) => (
          <SelectItem value={accountNumber} key={accountNumber}>
            <SelectInnerItem label={name} value={accountNumber} />
          </SelectItem>
        ))}
      </InputWithSelect>
    </>
  );
};

// Export Fields

export const renderTransferFieldsFrom = (
  values: IFormValues,
  accounts: IAccount[],
  state: ITransfersState,
  actions: ITransferActions,
  form: FormApi<IFormValues, Partial<IFormValues>>,
  submitting: boolean,
) => {
  const selectedSourceAccount = accounts.find((account) => account.accountNumber === values.sourceAccount);
  const amountField = renderAmountField(submitting, selectedSourceAccount?.currency);
  return (
    <>
      {renderSourceAccountField(accounts, submitting)}
      {amountField}
    </>
  );
};

export const renderTransferFieldsTo = (
  values: IFormValues,
  accounts: IAccount[],
  state: ITransfersState,
  actions: ITransferActions,
  form: FormApi<IFormValues, Partial<IFormValues>>,
  submitting: boolean,
  toggleScheduleTransfer: () => void,
  toggleSchedule: boolean | null,
  classes: Partial<ClassNameMap<FormControlLabelClassKey>> | ClassNameMap<'label' | 'root'> | undefined,
  dateFilterChange: (name: string, value?: string | null | undefined) => void,
  handleClearDateInput: (name: string, value?: string | null | undefined) => void,
) => (
  <>
    {renderAccountNumberField(values, state, actions.findBeneficiaryName, form)}
    {renderNarrationField(submitting)}
    {renderScheduleControl(toggleScheduleTransfer, toggleSchedule, classes)}
    {values.scheduleTransfer && renderScheduleForm(submitting, dateFilterChange, handleClearDateInput, values)}
  </>
);
